import { useState } from "react";
import { Heart, ThumbsDown, Send, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";
import ReportMenu from "@/components/ReportMenu";
import { timeAgo } from "@/lib/time";
import { tr } from "@/lib/translate";

/** Threaded comments (2 levels max). Backend stores parent_id flat. */
export default function PostComments({ postId, initial, onChange }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState(initial || []);
  const [text, setText] = useState("");
  const [replyOpenFor, setReplyOpenFor] = useState(null);
  const [replyText, setReplyText] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!user) { toast.error(tr("Sign in to comment")); return; }
    const { data } = await api.post(`/posts/${postId}/comments`, { content: text });
    setItems((c) => [...c, data]);
    setText("");
    if (onChange) onChange();
  };

  const sendReply = async (parentId, e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    if (!user) { toast.error(tr("Sign in to comment")); return; }
    const { data } = await api.post(`/posts/${postId}/comments`, { content: replyText, parent_id: parentId });
    setItems((c) => [...c, data]);
    setReplyText("");
    setReplyOpenFor(null);
  };

  const rate = async (commentId, kind) => {
    if (!user) { toast.error(tr("Sign in to interact")); return; }
    try {
      const { data } = await api.post(`/comments/${commentId}/${kind}`);
      setItems((c) => c.map((x) => x.id === commentId
        ? { ...x, likes: data.likes, dislikes: data.dislikes, liked_by_me: data.liked, disliked_by_me: data.disliked }
        : x));
    } catch { toast.error(tr("Could not update like")); }
  };

  const tops = items.filter((c) => !c.parent_id);

  return (
    <div className="border-t border-white/5 p-4 space-y-4">
      {tops.map((c) => {
        const replies = items.filter((r) => r.parent_id === c.id);
        return (
          <div key={c.id} data-testid={`comment-${c.id}`}>
            <CommentRow c={c} onRate={rate} t={t} canReply onReply={() => setReplyOpenFor(c.id)} />

            {replyOpenFor === c.id && (
              <form onSubmit={(e) => sendReply(c.id, e)} className="flex items-center gap-2 mt-2 ml-11">
                <input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={t.feed.writeReply} className="input-base" data-testid={`reply-input-${c.id}`} />
                <button type="submit" className="btn-primary py-2 px-4" data-testid={`reply-send-${c.id}`}>
                  <Send className="w-4 h-4" />
                </button>
              </form>
            )}

            {replies.length > 0 && (
              <div className="mt-2 space-y-2 ml-6 sm:ml-10">
                {replies.map((r) => (
                  <CommentRow key={r.id} c={r} onRate={rate} t={t} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      <form onSubmit={submit} className="flex items-center gap-2 pt-1">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t.feed.writeComment}
          className="input-base"
          data-testid={`comment-input-${postId}`}
        />
        <button type="submit" className="btn-primary py-2 px-4" data-testid={`comment-send-${postId}`}>
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function CommentRow({ c, onRate, t, canReply = false, onReply }) {
  return (
    <div className="flex items-start gap-3">
      <Avatar src={c.user_avatar} name={c.user_name} />
      <div className="flex-1 min-w-0">
        <div className="bg-zinc-900/60 border border-white/5 rounded-2xl px-3 py-2 inline-block max-w-full">
          <p className="text-sm">
            <span className="font-semibold text-white">{c.user_name}</span>{" "}
            <span className="text-zinc-300 break-words">{c.content}</span>
          </p>
        </div>
        <ReportMenu targetType="comment" targetId={c.id} authorId={c.user_id} authorName={c.user_name} className="!p-1 ml-1 align-middle inline-flex" />
        <div className="flex items-center gap-3 px-3 mt-1 text-xs text-zinc-500">
          <span>{timeAgo(c.created_at)}</span>
          <button
            onClick={() => onRate(c.id, "like")}
            className={`inline-flex items-center gap-1 ${c.liked_by_me ? "text-[#FF0050]" : "hover:text-white"}`}
            data-testid={`comment-like-${c.id}`}
          >
            <Heart className={`w-3 h-3 ${c.liked_by_me ? "fill-[#FF0050]" : ""}`} /> {c.likes || 0}
          </button>
          <button
            onClick={() => onRate(c.id, "dislike")}
            className={`inline-flex items-center gap-1 ${c.disliked_by_me ? "text-white" : "hover:text-white"}`}
            data-testid={`comment-dislike-${c.id}`}
          >
            <ThumbsDown className={`w-3 h-3 ${c.disliked_by_me ? "fill-white" : ""}`} /> {c.dislikes || 0}
          </button>
          {canReply && (
            <button onClick={onReply} className="hover:text-white inline-flex items-center gap-1" data-testid={`comment-reply-${c.id}`}>
              <CornerDownRight className="w-3 h-3" /> {t.feed.reply}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
